import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import {
  Button,
  TextField,
  Stack,
  Box,
  Container,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  Link as MuiLink,
  Alert,
  Card,
  CardContent,
  Fade,
  Slide,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login,
  PersonAdd,
  LockReset,
  Shield,
  WavingHand,
  Security,
} from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import * as Yup from "yup";
import Cookies from "js-cookie";
import { setAuthCookies, clearAuthCookies } from "../../../utils/api";
import RowadLogo from "../../RowadLogo";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import LoadingButton from "@mui/lab/LoadingButton";

const MySwal = withReactContent(Swal);

const EnhancedSignIn = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [LoadingAPI, setLoadingAPI] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const onSubmit = async (values) => {
    setLoadingAPI(true);
    let recaptchaToken = '';
    if (executeRecaptcha) {
      recaptchaToken = await executeRecaptcha('login');
    }
    axios
      .post(`${process.env.REACT_APP_Back_URL}/userst/authenticate`, {
        email: values.email,
        password: values.password,
        token: recaptchaToken,
      })
      .then((resp) => {
        if (resp.status === 200) {
          if (resp.data.icon === "success") {
            setAuthCookies(resp.data.accessToken, resp.data.refreshToken, resp.data.userId);
            MySwal.fire({
              icon: "success",
              title: "Login Successful",
              text: resp.data.message || "Login successful!",
              showConfirmButton: false,
              timer: 1500,
              background: '#51647C',
              color: 'white',
              customClass: {
                popup: 'animated-popup'
              }
            }).then(() => setTimeout(() => { navigate("/"); }, 200));
          } else {
            MySwal.fire({
              icon: resp.data.icon,
              title: "Oops!",
              text: resp.data.message,
              confirmButtonColor: '#B41736',
            });
            setLoadingAPI(false);
          }
        }
      })
      .catch((error) => {
        MySwal.fire({
          icon: "error",
          title: "Login Failed",
          text: "Please check your credentials and try again.",
          confirmButtonColor: '#B41736',
        });
        setLoadingAPI(false);
      });
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string().required("Password is required"),
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._%+-]+@(rowad-rme|rme-fitout|rme-g|driller-eg|rowad-rme\.net|g-buildings|rowad-investment|tetraes|orascom)\.(com|net)$/,
        "Invalid email - must be from an authorized domain"
      ),
  });

  const initialValues = {
    email: "",
    password: "",
    RememberMe: false,
  };

  useEffect(() => {
    navigate("/sign-in");
    if (isValidGUID(id) && id) {
      axios
        .patch(`${process.env.REACT_APP_Back_URL}/userst/status`, {
          statecode: 0,
          statuscode: 1,
          id,
        })
        .then((resp) => {
          if (resp.status === 200) {
            MySwal.fire({
              icon: "success",
              title: "Account Activated! ✅",
              text: "Your account has been successfully activated",
              showConfirmButton: false,
              timer: 1500,
            }).then(() => {
              clearAuthCookies();
              setTimeout(() => {
                navigate("/sign-in");
              }, 1500);
            });
          }
        })
        .catch((error) => {
          MySwal.fire({
            icon: "error",
            title: "Activation Failed",
            text: error.message,
          });
        });
    }
  }, []);

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
  });

  function isValidGUID(str) {
    let regex = new RegExp(
      /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/
    );
    if (str == null) {
      return false;
    }
    if (regex.test(str)) {
      return true;
    } else {
      return false;
    }
  }

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#51647C",
        position: "relative",
        overflow: "hidden",
        py: 4,
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          animation: "float 6s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          animation: "float 8s ease-in-out infinite",
        }}
      />

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            50% { transform: translateY(-20px) translateX(20px); }
          }
          @keyframes slideIn {
            from {
              transform: translateY(30px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      <Container maxWidth="sm">
        <Fade in={isVisible} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box sx={{ mb: 3 }}>
                <RowadLogo />
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                <WavingHand sx={{ fontSize: 32, mr: 1, color: "#51647C" }} />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#51647C",
                  }}
                >
                  Welcome To Rowad Ticketing System
                </Typography>
              </Box>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Sign in to access your support dashboard
              </Typography>

              {/* Security Badge */}
              <Card sx={{ bgcolor: "primary.light", color: "white", mb: 2 }}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <Security sx={{ fontSize: 20 }} />
                    <Typography variant="body2">
                      Secure Login - Your data is protected
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Login Form */}
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                {/* Email Field */}
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email Address"
                  placeholder="your.email@company.com"
                  {...formik.getFieldProps("email")}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 20px rgba(102, 126, 234, 0.25)",
                      },
                    },
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  placeholder="Enter your password"
                  {...formik.getFieldProps("password")}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePassword}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 4px 20px rgba(102, 126, 234, 0.25)",
                      },
                    },
                  }}
                />

                {/* Forgot Password Link */}
                <Box sx={{ textAlign: "right" }}>
                  <MuiLink
                    component={Link}
                    to="/reset-password"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <LockReset fontSize="small" />
                    Forgot Password?
                  </MuiLink>
                </Box>

                {/* Login Button */}
                <LoadingButton
                  fullWidth
                  size="large"
                  variant="contained"
                  loading={LoadingAPI}
                  type="submit"
                  startIcon={<Login />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    background: "#B41736",
                    boxShadow: "0 4px 15px rgba(180, 23, 54, 0.4)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#8f1129",
                      boxShadow: "0 6px 20px rgba(180, 23, 54, 0.6)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "grey.300",
                    },
                  }}
                >
                  Sign In
                </LoadingButton>

                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>

                {/* Create Account Button */}
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  startIcon={<PersonAdd />}
                  onClick={() => navigate("/sign-up")}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderWidth: 2,
                    borderColor: "primary.main",
                    color: "primary.main",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderWidth: 2,
                      borderColor: "primary.dark",
                      bgcolor: "primary.light",
                      color: "white",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                    },
                  }}
                >
                  Create New Account
                </Button>
              </Stack>
            </form>

            {/* Footer */}
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} ROWAD RME. All rights reserved.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default EnhancedSignIn;
